import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Box, Button, Card, TextField } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDelete from '../../../shared/components/ConfirmDelete';
import CustomDataGrid from '../../../shared/components/CustomDataGrid';
import { handleError } from '../../../shared/functions/handleError';
import { useEmployeeSkillColumns } from '../../../shared/hooks/useEmployeeSkillColumns';
import { usePagination } from '../../../shared/hooks/usePagination';
import { IEmployeeSkills } from '../../../shared/interfaces/employeeSkills/IEmployeeSkills';
import EmployeeSkillFormModal from './components/EmployeeSkillFormModal';
import { AdminEmployeeSkillsService } from './service';

const EmployeeSkills = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<IEmployeeSkills[] | undefined>(undefined);
  const [selectedSkill, setSelectedSkill] = useState<IEmployeeSkills | null>(
    null,
  );
  const [isAddModalOpened, setIsAddModalOpened] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    changeFilterHandler,
    pagination,
    setCount,
    totalPages,
    setTotalPages,
    count,
    handleUpdateList,
    setPagination,
  } = usePagination({
    perPage: 15,
  });

  const columns = useEmployeeSkillColumns(
    setSelectedSkill,
    setShowDeleteModal,
    setIsAddModalOpened,
  );

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedSkill(null);
  };

  const deleteHandler = async (id: number) => {
    await AdminEmployeeSkillsService.delete(id)
      .then(() => {
        enqueueSnackbar(
          t('toasts.successfullyDeleted', { title: selectedSkill?.name }),
          {
            variant: 'success',
          },
        );
        handleCloseDeleteModal();
        handleUpdateList();
      })
      .catch(handleError);
  };

  const handleSubmit = () => {
    if (selectedSkill?.id) {
      AdminEmployeeSkillsService.update(selectedSkill, selectedSkill?.id)
        .then((response) => {
          enqueueSnackbar(
            t('toasts.successfullyUpdated', { title: response?.name }),
            {
              variant: 'success',
            },
          );
          handleUpdateList();

          setIsAddModalOpened(false);
          setSelectedSkill(null);
        })
        .catch(handleError);
    } else {
      AdminEmployeeSkillsService.create(selectedSkill as IEmployeeSkills)
        .then(() => {
          enqueueSnackbar(
            t('toasts.successfullyCreated', { title: selectedSkill?.name }),
            {
              variant: 'success',
            },
          );
          handleUpdateList();
          setIsAddModalOpened(false);
          setSelectedSkill(null);
        })
        .catch(handleError);
    }
  };
  useEffect(() => {
    AdminEmployeeSkillsService.get({
      ...pagination,
      page: pagination?.page + 1,
    })
      .then((response) => {
        setCount(response?.count);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
        setData(response.data);
      })
      .catch(handleError);
  }, [pagination, setTotalPages, setCount]);

  return (
    <Card sx={{ marginTop: 5 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '20px',
        }}
      >
        <TextField
          onChange={(e) => {
            changeFilterHandler('name', e.target.value);
          }}
          label={t('general.searchByName')}
          sx={{
            width: { xs: '100%', md: 'auto' },
            paddingBottom: { xs: 2, md: 0 },
          }}
        />

        <Button
          variant={'contained'}
          onClick={() => setIsAddModalOpened(true)}
          sx={{ width: { xs: '100%', md: 'auto' } }}
        >
          <AddCircleOutlineOutlinedIcon sx={{ marginRight: '5px' }} />
          {t('button.addNewSkill')}
        </Button>
      </Box>
      <CustomDataGrid
        hasNextPage={totalPages < pagination?.page}
        rows={data || []}
        columns={columns}
        rowCount={count}
        pagination={pagination}
        setPagination={setPagination}
        loading={data === undefined}
      />

      <EmployeeSkillFormModal
        open={isAddModalOpened}
        handleClose={() => {
          setSelectedSkill(null);
          setIsAddModalOpened(false);
        }}
        handleSubmit={handleSubmit}
        selectedSkill={selectedSkill as IEmployeeSkills}
        setSelectedSkill={setSelectedSkill}
        isUpdate={!!selectedSkill?.id}
      />

      <ConfirmDelete
        open={showDeleteModal}
        title={selectedSkill?.name}
        handleClose={handleCloseDeleteModal}
        handleSubmit={() => deleteHandler(Number(selectedSkill?.id))}
      />
    </Card>
  );
};

export default EmployeeSkills;
